// 達成度判断表の「表示専用」変換（取得済みデータのクライアント整形 / A3: カテゴリ c）。
// データ取得・既定の並び順・セル値・色判定は不変。検索/ソートは表示変換のみで、
// 解除（query 空 + sort null）すると入力をそのまま返す＝恒等となる（既定表示へ復帰）。

import type { AssessmentColorKey } from 'utils/assessment';

export interface MatrixCellView {
    phenomenonId: string;
    text: string; // 表示テキスト（例 "-2.5 dB" / "-"）
    copyText: string; // クリックコピー用（単位なしの数値のみ。例 "-2.5" / "-"）
    colorKey: AssessmentColorKey;
}

export interface MatrixRowView {
    driveModeId: string;
    label: string; // drive mode 表示ラベル
    cells: MatrixCellView[];
    searchText: string; // 検索対象（unit ラベル + mode ラベル + セル値、小文字化済み）
}

export interface MatrixGroupView {
    unitId: string;
    label: string; // unit 表示ラベル
    rows: MatrixRowView[];
}

export type MatrixSortDir = 'asc' | 'desc';

// columnId は phenomenonId、または先頭列（mode ラベル）の MODE_SORT_KEY。
export const MODE_SORT_KEY = '__mode__';

export interface MatrixSort {
    columnId: string;
    dir: MatrixSortDir;
}

// 自然順コレーター（mode ラベルのソート用）。
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

// "-2.5 dB" → -2.5、"-" → null。ソート用の数値抽出。
export const parseDeviation = (text: string): number | null => {
    const m = text.match(/-?\d+(?:\.\d+)?/);
    return m ? Number(m[0]) : null;
};

// 検索: query 空なら恒等。それ以外は各 group の rows を searchText 部分一致で絞り込み、空 group を除く。
export const filterGroups = (groups: MatrixGroupView[], query: string): MatrixGroupView[] => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
        .map((g) => ({ ...g, rows: g.rows.filter((r) => r.searchText.includes(q)) }))
        .filter((g) => g.rows.length > 0);
};

const compareRows = (a: MatrixRowView, b: MatrixRowView, sort: MatrixSort): number => {
    const sign = sort.dir === 'asc' ? 1 : -1;
    if (sort.columnId === MODE_SORT_KEY) {
        return collator.compare(a.label, b.label) * sign;
    }
    const av = parseDeviation(a.cells.find((c) => c.phenomenonId === sort.columnId)?.text ?? '');
    const bv = parseDeviation(b.cells.find((c) => c.phenomenonId === sort.columnId)?.text ?? '');
    // 値なし（"-"）は方向に依らず常に末尾へ寄せる。
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return (av - bv) * sign;
};

// ソート: sort null なら恒等。group の並びは保持し、各 group 内の drive mode 行のみ並べ替える。
export const sortGroups = (groups: MatrixGroupView[], sort: MatrixSort | null): MatrixGroupView[] => {
    if (!sort) return groups;
    return groups.map((g) => ({ ...g, rows: [...g.rows].sort((a, b) => compareRows(a, b, sort)) }));
};

// 検索 → ソートの順に適用。query 空 + sort null のとき入力をそのまま返す（恒等）。
export const applyMatrixView = (
    groups: MatrixGroupView[],
    query: string,
    sort: MatrixSort | null,
): MatrixGroupView[] => sortGroups(filterGroups(groups, query), sort);
